import React, { useMemo, useCallback, useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import DropdownMenuStateless, { DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { CustomItem } from '@atlaskit/menu';
import Tooltip from '@atlaskit/tooltip';

import { ComponentLinkProxy } from 'src/components/component-link';
import { RunPipelineContext } from 'src/components/pipelines/pages/modals/run-pipeline-modal';
import { ConnectBranchActions } from 'src/connect/branch-modules';
import { useIntl } from 'src/hooks/intl';
import {
  onToggleBulkDeleteSelectionMode,
  openDeleteBranchDialog,
  openSyncBranchDialog,
  openMergeBranchDialog,
  RELOAD_BRANCHES,
  getMainBranch,
  getBranchingModel,
} from 'src/redux/branches';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import { SyncStrategyContext } from 'src/sections/repository/sections/branches/components/branch-list';
import repoUrls from 'src/sections/repository/urls';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import sourceUrls from 'src/urls/source';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { DetailedBranch } from '../../types';
import branchUrls from '../../urls';

import messages from './branch-actions.i18n';
import * as styles from './branch-actions.styled';

export type BranchActionsBranch = Pick<
  DetailedBranch,
  | 'name'
  | 'ahead'
  | 'behind'
  | 'permissions'
  | 'isMainBranch'
  | 'pullRequests'
  | 'pullRequestsTargetedCount'
  | 'sync_strategies'
> & {
  target: {
    hash: string;
  };
};

export type BranchActionProps = {
  allowBulkDelete: boolean;
  branch: BranchActionsBranch;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export const BranchActions = ({
  branch,
  allowBulkDelete,
  isOpen,
  onOpenChange,
}: BranchActionProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const fullSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const mainBranch = useSelector(getMainBranch);
  const branchingModel = useSelector(getBranchingModel);
  const setSyncStrategies = useContext(SyncStrategyContext);

  const [sourceUrl, compareUrl, createPullRequestUrl] = useMemo(() => {
    if (!fullSlug) {
      return ['', '', ''];
    }
    const [owner, slug] = fullSlug.split('/');
    return [
      sourceUrls.ui.sourceWithRef(fullSlug || '', branch),
      branchUrls.ui.compareBranch(fullSlug, branch.name, {
        compareWith: branchingModel.development?.name,
      }),
      `${repoUrls.ui.createPullRequest(
        owner || '',
        slug || '',
        branch.name
      )}&event_source=branch_list`,
    ];
  }, [branch, fullSlug, branchingModel.development?.name]);

  const onOpenBranchDeleteDialog = useCallback(() => {
    dispatch(openDeleteBranchDialog(branch.name));
  }, [branch, dispatch]);

  const onOpenSyncBranchDialog = useCallback(() => {
    if (setSyncStrategies) {
      setSyncStrategies(branch?.sync_strategies);
    }

    dispatch(
      openSyncBranchDialog({
        sourceBranchName: mainBranch.name,
        sourceRepositoryFullName: fullSlug,
        destinationBranchName: branch.name,
        destinationRepositoryFullName: fullSlug,
        reloadAction: { type: RELOAD_BRANCHES },
      })
    );
  }, [branch, mainBranch, fullSlug, dispatch, setSyncStrategies]);

  const onMergeBranchClick = useCallback(() => {
    dispatch(
      openMergeBranchDialog({
        sourceBranchName: branch.name,
        sourceRepositoryFullName: fullSlug,
        destinationBranchName: mainBranch.name,
        destinationRepositoryFullName: fullSlug,
        reloadAction: { type: RELOAD_BRANCHES },
        canDeleteSourceBranch: branch.permissions?.delete === 'allow',
      })
    );
  }, [branch, mainBranch, fullSlug, dispatch]);

  const deleteMultipleBranchesClick = useCallback(() => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'deleteMultipleBranches',
      source: 'branchesScreenBranchActions',
    });
    dispatch(onToggleBulkDeleteSelectionMode(true));
  }, [dispatch]);

  const renderDeleteActions = useCallback(() => {
    const { isMainBranch } = branch;

    let disabledMessage;
    if (isMainBranch) {
      disabledMessage = messages.deleteDisabledMainBranch;
    } else if (branch.permissions.delete !== 'allow') {
      disabledMessage = messages.deleteDisabledPermissions;
    } else if (
      branch.pullRequestsTargetedCount > 0 ||
      branch.pullRequests.length > 0
    ) {
      disabledMessage = messages.deleteDisabledPullRequests;
    }

    if (disabledMessage) {
      return (
        <Tooltip content={<FormattedMessage {...disabledMessage} />}>
          <DropdownItem isDisabled>
            <FormattedMessage {...messages.actionDelete} />
          </DropdownItem>
        </Tooltip>
      );
    }

    return (
      <>
        <DropdownItem onClick={onOpenBranchDeleteDialog}>
          <FormattedMessage {...messages.actionDelete} />
        </DropdownItem>
        {allowBulkDelete && (
          <DropdownItem onClick={deleteMultipleBranchesClick}>
            <FormattedMessage {...messages.deleteMultiple} />
          </DropdownItem>
        )}
      </>
    );
  }, [
    branch,
    allowBulkDelete,
    onOpenBranchDeleteDialog,
    deleteMultipleBranchesClick,
  ]);

  const renderRunPipelineAction = useCallback(
    () => (
      <RunPipelineContext.Consumer>
        {({ open }) => (
          <DropdownItem
            onClick={() =>
              open({ refName: branch.name, revision: branch.target.hash })
            }
          >
            <FormattedMessage {...messages.actionRunPipeline} />
          </DropdownItem>
        )}
      </RunPipelineContext.Consumer>
    ),
    [branch.name, branch.target.hash]
  );

  const renderMergeAction = useCallback(() => {
    if (!branch.ahead || branch.ahead <= 0) {
      return null;
    }

    const allowMerge = mainBranch.permissions.push === 'allow';

    if (allowMerge) {
      return (
        <DropdownItem onClick={onMergeBranchClick}>
          <FormattedMessage {...messages.actionMerge} />
        </DropdownItem>
      );
    }

    return (
      <Tooltip
        content={
          <FormattedMessage
            {...messages.mergeDisabled}
            values={{ destination: mainBranch.name }}
          />
        }
      >
        <DropdownItem isDisabled>
          <FormattedMessage {...messages.actionMerge} />
        </DropdownItem>
      </Tooltip>
    );
  }, [branch, mainBranch, onMergeBranchClick]);

  const renderSyncAction = useCallback(() => {
    if (!branch.behind || branch.behind <= 0) {
      return null;
    }

    const allowSync = branch.permissions.push === 'allow';

    if (allowSync) {
      return (
        <DropdownItem onClick={onOpenSyncBranchDialog}>
          <FormattedMessage {...messages.actionSync} />
        </DropdownItem>
      );
    }

    return (
      <Tooltip
        content={
          <FormattedMessage
            {...messages.syncDisabled}
            values={{ source: mainBranch.name }}
          />
        }
      >
        <DropdownItem isDisabled>
          <FormattedMessage {...messages.actionSync} />
        </DropdownItem>
      </Tooltip>
    );
  }, [branch, mainBranch, onOpenSyncBranchDialog]);

  const renderBranchPullRequestDropdownItem = useCallback(() => {
    if (!branch.pullRequests.length) {
      return null;
    }

    const [pullRequest] = branch.pullRequests;
    return (
      <styles.AdaptiveDropdownItem>
        <DropdownItem
          title={pullRequest.title}
          href={pullRequest.links.html.href}
        >
          <FormattedMessage {...messages.actionViewPullRequest} />
        </DropdownItem>
      </styles.AdaptiveDropdownItem>
    );
  }, [branch]);

  const renderCreatePullRequestDropdownItem = useCallback(() => {
    let canCreatePR = !branch.isMainBranch;

    if (branch.ahead != null) {
      canCreatePR = canCreatePR && branch.ahead > 0;
    }

    return canCreatePR ? (
      <RepositoryAccessGuard requiredLevel="write">
        <DropdownItem href={createPullRequestUrl}>
          <FormattedMessage {...messages.actionCreatePullRequest} />
        </DropdownItem>
      </RepositoryAccessGuard>
    ) : null;
  }, [branch, createPullRequestUrl]);

  const handleOpenChange = useCallback(
    (event: any) => onOpenChange(event.isOpen),
    [onOpenChange]
  );

  return (
    <DropdownMenuStateless
      placement="bottom-end"
      spacing="compact"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      trigger={({ triggerRef, ...props }) => (
        <Button
          {...props}
          ref={triggerRef}
          spacing="compact"
          appearance="subtle"
          iconBefore={<MoreIcon size="small" label="" />}
          aria-label={intl.formatMessage(messages.branchActionsButtonLabel)}
          aria-haspopup="true"
          aria-expanded={isOpen}
          isSelected={isOpen}
        />
      )}
    >
      <CustomItem href={sourceUrl} component={ComponentLinkProxy}>
        <FormattedMessage {...messages.actionViewSource} />
      </CustomItem>

      <DropdownItem
        href={compareUrl}
        onClick={() => {
          publishUiEvent({
            action: 'clicked',
            actionSubject: 'dropdownItem',
            actionSubjectId: 'compareBranchDropdownItem',
            source: 'branchesScreen',
          });
        }}
      >
        <FormattedMessage {...messages.actionCompare} />
      </DropdownItem>

      <RepositoryAccessGuard requiredLevel="write">
        {renderMergeAction()}
        {renderSyncAction()}
        {renderDeleteActions()}
      </RepositoryAccessGuard>
      {renderRunPipelineAction()}
      <ConnectBranchActions name={branch.name} component={DropdownItem} />
      {renderBranchPullRequestDropdownItem()}
      {renderCreatePullRequestDropdownItem()}
    </DropdownMenuStateless>
  );
};
